import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.scss';

// Centralized input roles - see craftingSystem.ts's CraftInputRole doc
// comment. Every minigame (smith today, the rest later) only ever deals in
// these five roles, never a raw key - rebinding later just means changing
// this one map, no minigame-specific code to touch.
type CraftInputRole = 'primary' | 'secondary' | 'left' | 'right' | 'cancel';
const CRAFT_KEYBINDS: Record<string, CraftInputRole> = {
  Space: 'primary',
  KeyE: 'secondary',
  KeyA: 'left',
  KeyD: 'right',
  Escape: 'cancel',
};

interface MinigameIngredient { itemFormId: string; name: string; count: number }

interface ForgeHitInit { windowStartMs: number; windowEndMs: number }
interface ForgeInit {
  idealMin: number; idealMax: number;
  hits: ForgeHitInit[];
  tempPhaseMaxMs: number; hammerPhaseMaxMs: number; quenchPhaseMaxMs: number;
  quenchWindowStartMs: number; quenchWindowEndMs: number;
}
interface GenericStepInit { kind: 'gauge' | 'timing'; targetMin: number; targetMax: number; windowStartMs: number; windowEndMs: number; durationMs: number }
interface GenericInit { steps: GenericStepInit[] }

interface StartDetail {
  sessionId: string;
  activity: string;
  recipeName: string;
  ingredients: MinigameIngredient[];
  difficulty: number;
  durationMs: number;
  minigame: ForgeInit | GenericInit;
}

type ForgeSync = {
  phase: 'temperature' | 'hammer' | 'quench' | 'done';
  phaseElapsedMs: number;
  temperature: number;
  nextHitIndex: number;
  hitGrades: (Grade | null)[];
  quenchGrade: Grade | null;
};
type GenericSync = { index: number; phaseElapsedMs: number; needle: number; grades: (number | null)[] };
type Grade = 'perfect' | 'good' | 'late' | 'miss';

interface SyncDetail { sessionId: string; progress: number; state: ForgeSync | GenericSync }
interface EndDetail { sessionId: string; cancelled?: boolean; result?: { success: boolean; quality: number; mistakes: number } }

const CRAFT_ACTION_PREFIX = 'craft-action:';

const ACTIVITY_TITLE: Record<string, string> = {
  smith: 'Forge', smelt: 'Fonderie', cook: 'Cuisine', brew: 'Alchimie', tan: 'Tannerie', weave: 'Tissage',
};
const ACTIVITY_SUBTITLE: Record<string, string> = {
  smith: 'Façonnez le métal. Donnez-lui un but.',
  smelt: "Le minerai n'attend pas ceux qui hésitent.",
  cook: 'Un bon plat se mérite.',
  brew: 'Le dosage fait tout.',
  tan: 'Le cuir se travaille avec patience.',
  weave: 'Chaque fil compte.',
};

const GRADE_LABEL: Record<Grade, string> = { perfect: 'Parfait', good: 'Bien', late: 'En retard', miss: 'Raté' };

function isForgeMinigame(activity: string): boolean {
  return activity === 'smith';
}

const CraftingSession: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [start, setStart] = useState<StartDetail | null>(null);
  const [sync, setSync] = useState<SyncDetail | null>(null);
  const [progress, setProgress] = useState(0);
  const [liveQuality, setLiveQuality] = useState(1);
  const [result, setResult] = useState<EndDetail['result'] | null>(null);
  const [ended, setEnded] = useState(false);

  const heldRef = useRef<Record<CraftInputRole, boolean>>({ primary: false, secondary: false, left: false, right: false, cancel: false });

  const sendAction = useCallback((sessionId: string, action: CraftInputRole, pressed?: boolean) => {
    const w = window as unknown as { skyrimPlatform?: { sendMessage?: (m: string) => void } };
    w.skyrimPlatform?.sendMessage?.(CRAFT_ACTION_PREFIX + JSON.stringify({ sessionId, action, pressed, timestamp: Date.now() }));
  }, []);

  useEffect(() => {
    const onStart = (e: Event) => {
      const d = (e as CustomEvent).detail as StartDetail;
      setStart(d);
      setSync(null);
      setProgress(0);
      setLiveQuality(1);
      setResult(null);
      setEnded(false);
      heldRef.current = { primary: false, secondary: false, left: false, right: false, cancel: false };
      setVisible(true);
    };
    const onSync = (e: Event) => {
      const d = (e as CustomEvent).detail as SyncDetail;
      setSync(d);
      setProgress(d.progress);
    };
    const onEnd = (e: Event) => {
      const d = (e as CustomEvent).detail as EndDetail;
      setResult(d.result ?? null);
      setEnded(true);
      // Give the player a beat to read the outcome before the overlay is
      // torn down by the client relay's own closeOverlay() (which fires
      // right after this same event, independently).
      setTimeout(() => setVisible(false), 1600);
    };
    const onHide = () => setVisible(false);

    window.addEventListener('skymp5-client:showCraftingSession', onStart);
    window.addEventListener('skymp5-client:craftingSessionSync', onSync);
    window.addEventListener('skymp5-client:craftingSessionEnd', onEnd);
    window.addEventListener('skymp5-client:hideCraftingSession', onHide);
    return () => {
      window.removeEventListener('skymp5-client:showCraftingSession', onStart);
      window.removeEventListener('skymp5-client:craftingSessionSync', onSync);
      window.removeEventListener('skymp5-client:craftingSessionEnd', onEnd);
      window.removeEventListener('skymp5-client:hideCraftingSession', onHide);
    };
  }, []);

  // Live quality estimate for the "jauge de qualité" the player watches
  // during play - computed from the same real, server-synced grades the
  // final authoritative score will use (never a fabricated/random number),
  // just recomputed continuously here instead of once at the end. Replaced
  // outright by the server's own number the instant craftSessionEnd
  // arrives (see the `result` branch in the quality bar's render below).
  useEffect(() => {
    if (!sync || !start) return;
    if (isForgeMinigame(start.activity)) {
      const s = sync.state as ForgeSync;
      const gradeScore: Record<Grade, number> = { perfect: 1, good: 0.7, late: 0.35, miss: 0 };
      const graded = s.hitGrades.filter((g): g is Grade => g !== null);
      const hammerScore = graded.length ? graded.reduce((sum, g) => sum + gradeScore[g], 0) / graded.length : null;
      const quenchScore = s.quenchGrade ? gradeScore[s.quenchGrade] : null;
      const parts = [hammerScore, quenchScore].filter((x): x is number => x !== null);
      if (parts.length) setLiveQuality(parts.reduce((a, b) => a + b, 0) / parts.length);
    } else {
      const s = sync.state as GenericSync;
      const graded = s.grades.filter((g): g is number => g !== null);
      if (graded.length) setLiveQuality(graded.reduce((a, b) => a + b, 0) / graded.length);
    }
  }, [sync, start]);

  // Keyboard -> role mapping. Held-state roles (primary/secondary) report
  // both press and release; the rest only report a press.
  useEffect(() => {
    if (!visible || !start || ended) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const role = CRAFT_KEYBINDS[e.code];
      if (!role) return;
      e.preventDefault();
      if (role === 'primary' || role === 'secondary') {
        if (heldRef.current[role]) return; // key-repeat guard, not a fresh press
        heldRef.current[role] = true;
      }
      sendAction(start.sessionId, role, true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const role = CRAFT_KEYBINDS[e.code];
      if (!role) return;
      if (role === 'primary' || role === 'secondary') {
        heldRef.current[role] = false;
        sendAction(start.sessionId, role, false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [visible, start, ended, sendAction]);

  if (!visible || !start) return null;

  const quality = ended && result ? result.quality / 100 : liveQuality;
  const title = ACTIVITY_TITLE[start.activity] ?? 'Fabrication';
  const subtitle = ACTIVITY_SUBTITLE[start.activity] ?? 'Fabrique avec soin.';

  return (
    <div className="crafting-session-overlay">
      <div className={`crafting-session-shell${ended ? (result?.success ? ' crafting-session-shell--ok' : ' crafting-session-shell--fail') : ''}`}>
        <header className="crafting-session-header">
          <div>
            <div className="crafting-session-header__title">{title}</div>
            <div className="crafting-session-header__subtitle">{subtitle}</div>
          </div>
          <div className="crafting-session-header__recipe">{start.recipeName}</div>
        </header>

        <div className="crafting-session-body">
          <aside className="crafting-session-materials">
            <div className="crafting-session-materials__heading">Matériaux engagés</div>
            {start.ingredients.map((ing, i) => (
              <div className="crafting-session-material" key={ing.itemFormId + i}>
                <span className="crafting-session-material__name">{ing.name}</span>
                <span className="crafting-session-material__count">×{ing.count}</span>
              </div>
            ))}
          </aside>

          <main className="crafting-session-stage">
            {ended ? (
              <div className="crafting-session-outcome">
                <div className={`crafting-session-outcome__title${result?.success ? ' crafting-session-outcome__title--ok' : ' crafting-session-outcome__title--fail'}`}>
                  {result?.success ? 'Fabrication réussie' : 'Fabrication échouée'}
                </div>
                {result && (
                  <div className="crafting-session-outcome__score">
                    <span className="crafting-session-outcome__number">{result.quality}</span>
                    <span className="crafting-session-outcome__unit">% de qualité</span>
                  </div>
                )}
              </div>
            ) : isForgeMinigame(start.activity) ? (
              <ForgeStage init={start.minigame as ForgeInit} sync={sync?.state as ForgeSync | undefined} />
            ) : (
              <GenericStage init={start.minigame as GenericInit} sync={sync?.state as GenericSync | undefined} />
            )}

            {!ended && (
              <div className="crafting-session-hint">
                {isForgeMinigame(start.activity) ? forgeHint(sync?.state as ForgeSync | undefined) : <><kbd>Espace</kbd> agir au bon moment</>}
                <span className="crafting-session-hint__sep"><kbd>Échap</kbd> annuler</span>
              </div>
            )}
          </main>
        </div>

        <footer className="crafting-session-footer">
          <div className="crafting-session-bar">
            <div className="crafting-session-bar__label">Progression</div>
            <div className="crafting-session-bar__track">
              <div className="crafting-session-bar__fill crafting-session-bar__fill--progress" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
          <div className="crafting-session-bar">
            <div className="crafting-session-bar__label">Qualité</div>
            <div className="crafting-session-bar__track">
              <div className="crafting-session-bar__fill crafting-session-bar__fill--quality" style={{ width: `${Math.round(quality * 100)}%` }} />
            </div>
            <div className="crafting-session-bar__value">{Math.round(quality * 100)}%</div>
          </div>
        </footer>
      </div>
    </div>
  );
};

function forgeHint(state: ForgeSync | undefined): React.ReactNode {
  if (!state) return null;
  if (state.phase === 'temperature') return <><kbd>Espace</kbd> chauffer · <kbd>E</kbd> refroidir</>;
  if (state.phase === 'hammer') return <><kbd>Espace</kbd> frapper au bon moment</>;
  if (state.phase === 'quench') return <><kbd>Espace</kbd> tremper la pièce</>;
  return null;
}

const FORGE_STEPS: { id: ForgeSync['phase']; label: string }[] = [
  { id: 'temperature', label: 'Chauffe' },
  { id: 'hammer', label: 'Frappe' },
  { id: 'quench', label: 'Trempe' },
];

const ForgeSteps: React.FC<{ phase: ForgeSync['phase'] }> = ({ phase }) => {
  const current = FORGE_STEPS.findIndex((st) => st.id === phase);
  return (
    <ol className="crafting-session-steps">
      {FORGE_STEPS.map((st, i) => (
        <li
          key={st.id}
          className={'crafting-session-steps__item' + (i === current ? ' crafting-session-steps__item--current' : i < current ? ' crafting-session-steps__item--done' : '')}
        >
          {st.label}
        </li>
      ))}
    </ol>
  );
};

const ForgeStage: React.FC<{ init: ForgeInit; sync?: ForgeSync }> = ({ init, sync }) => {
  const phase = sync?.phase ?? 'temperature';

  if (phase === 'temperature' || !sync) {
    const temperature = sync?.temperature ?? 25;
    return (
      <>
        <ForgeSteps phase={phase} />
        <div className="crafting-session-gauge-heading">
          <span>Température</span>
          <span className="crafting-session-gauge-heading__value">{Math.round(400 + (temperature / 100) * 800)}°C</span>
        </div>
        <div className="crafting-session-gauge">
          <div className="crafting-session-gauge__zone" style={{ left: `${init.idealMin}%`, width: `${init.idealMax - init.idealMin}%` }} />
          <div className="crafting-session-gauge__needle" style={{ left: `${temperature}%` }} />
        </div>
        <div className="crafting-session-gauge-labels">
          <span>Trop froid</span>
          <span className="crafting-session-gauge-labels__mid">Zone idéale</span>
          <span>Trop chaud</span>
        </div>
      </>
    );
  }

  if (phase === 'hammer') {
    const elapsed = sync.phaseElapsedMs;
    return (
      <>
        <ForgeSteps phase={phase} />
        <div className="crafting-session-gauge-heading"><span>Rythme de frappe</span></div>
        <div className="crafting-session-rhythm">
          {init.hits.map((hit, i) => {
            const centerPct = ((hit.windowStartMs + hit.windowEndMs) / 2 / init.hammerPhaseMaxMs) * 100;
            const grade = sync.hitGrades[i];
            const isNext = i === sync.nextHitIndex;
            return (
              <div
                key={i}
                className={`crafting-session-rhythm__point${grade ? ` crafting-session-rhythm__point--${grade}` : ''}${isNext ? ' crafting-session-rhythm__point--next' : ''}`}
                style={{ left: `${centerPct}%` }}
              >
                {i + 1}
                {grade && <span className="crafting-session-rhythm__grade">{GRADE_LABEL[grade]}</span>}
              </div>
            );
          })}
          <div className="crafting-session-rhythm__marker" style={{ left: `${Math.min(100, (elapsed / init.hammerPhaseMaxMs) * 100)}%` }} />
        </div>
      </>
    );
  }

  // quench
  const elapsed = sync.phaseElapsedMs;
  return (
    <>
      <ForgeSteps phase={phase} />
      <div className="crafting-session-gauge-heading"><span>Trempe</span></div>
      <div className="crafting-session-timing">
        <div
          className="crafting-session-timing__zone"
          style={{
            left: `${(init.quenchWindowStartMs / init.quenchPhaseMaxMs) * 100}%`,
            width: `${((init.quenchWindowEndMs - init.quenchWindowStartMs) / init.quenchPhaseMaxMs) * 100}%`,
          }}
        />
        <div className="crafting-session-timing__marker" style={{ left: `${Math.min(100, (elapsed / init.quenchPhaseMaxMs) * 100)}%` }} />
      </div>
      {sync.quenchGrade && <div className="crafting-session-quench-grade">{GRADE_LABEL[sync.quenchGrade]}</div>}
    </>
  );
};

const GenericStage: React.FC<{ init: GenericInit; sync?: GenericSync }> = ({ init, sync }) => {
  const index = sync?.index ?? 0;
  const step = init.steps[index];
  if (!step) return null;

  if (step.kind === 'gauge') {
    const needle = sync?.needle ?? 50;
    return (
      <>
        <div className="crafting-session-gauge-heading"><span>Maintiens la jauge dans la zone</span></div>
        <div className="crafting-session-gauge">
          <div className="crafting-session-gauge__zone" style={{ left: `${step.targetMin}%`, width: `${step.targetMax - step.targetMin}%` }} />
          <div className="crafting-session-gauge__needle" style={{ left: `${needle}%` }} />
        </div>
      </>
    );
  }

  const elapsed = sync?.phaseElapsedMs ?? 0;
  return (
    <>
      <div className="crafting-session-gauge-heading"><span>Appuie au bon moment</span></div>
      <div className="crafting-session-timing">
        <div
          className="crafting-session-timing__zone"
          style={{ left: `${(step.windowStartMs / step.durationMs) * 100}%`, width: `${((step.windowEndMs - step.windowStartMs) / step.durationMs) * 100}%` }}
        />
        <div className="crafting-session-timing__marker" style={{ left: `${Math.min(100, (elapsed / step.durationMs) * 100)}%` }} />
      </div>
    </>
  );
};

export default CraftingSession;
